/**
 * Where a card was dropped, as the card it now sits next to.
 *
 * The board shows only some of a column: archived tickets and drafts are
 * hidden, and so is anything the board's filters or search leave out. They
 * all keep their places on the server, so the index of the drop among the
 * cards shown, applied there, put the card off by the hidden tickets above the
 * slot (TOADO-228). The card it was dropped in front of, or after at the
 * bottom of the column, is the same place however many hidden tickets there
 * are, and the server finds it among them.
 *
 * `columnCards` is the destination column as the board shows it, with the
 * dropped card already in its new place. Nothing is returned when the card is
 * alone there: it goes at the end, as without an anchor.
 */
export type DropAnchor = { beforeTicketId: string } | { afterTicketId: string };

export function dropAnchor(columnCards: ReadonlyArray<{ id: string }>, ticketId: string): DropAnchor | undefined {
  const at = columnCards.findIndex((card) => card.id === ticketId);
  if (at === -1) return undefined;
  const next = columnCards[at + 1];
  if (next) return { beforeTicketId: next.id };
  const previous = columnCards[at - 1];
  if (previous) return { afterTicketId: previous.id };
  return undefined;
}

/**
 * The list with `ticketId` moved into `columnId` where the server puts it for
 * the same anchor: in front of or after the anchor card, and at the end of the
 * column without one. The board's cache is placed this way so the order the
 * person sees until the refetch is the order the refetch brings.
 *
 * A list that does not hold the anchor in that column is returned unchanged,
 * as is one that does not hold the ticket: where the card goes in it is not
 * known. A board list for another type filter is one, since the anchor can be
 * of a type it leaves out, and so is the board list under a search, which
 * shows cards from the search's own results.
 */
export function placeAtAnchor<T extends { id: string; columnId: string }>(
  list: T[],
  ticketId: string,
  columnId: T['columnId'],
  anchor: DropAnchor | undefined,
): T[] {
  const moving = list.find((t) => t.id === ticketId);
  if (!moving) return list;
  const moved = { ...moving, columnId };
  const without = list.filter((t) => t.id !== ticketId);
  let insertAt: number;
  if (anchor) {
    const anchorId = 'beforeTicketId' in anchor ? anchor.beforeTicketId : anchor.afterTicketId;
    const anchorAt = without.findIndex((t) => t.id === anchorId && t.columnId === columnId);
    if (anchorAt === -1) return list;
    insertAt = 'beforeTicketId' in anchor ? anchorAt : anchorAt + 1;
  } else {
    let lastInColumn = -1;
    without.forEach((t, i) => { if (t.columnId === columnId) lastInColumn = i; });
    insertAt = lastInColumn === -1 ? without.length : lastInColumn + 1;
  }
  return [...without.slice(0, insertAt), moved, ...without.slice(insertAt)];
}

/**
 * `current` with only `ticketId` put back where it was in `previous`: in its
 * old column, beside the neighbour it had there that is still in that column.
 * Everything else in `current` is kept, so a refused move does not undo what
 * live events changed while it was in flight, and the ticket keeps its current
 * fields too.
 *
 * `placedIn` is the column the move put it in. A ticket found anywhere else
 * has been moved since by somebody else, and stays where they put it. A
 * ticket `current` no longer holds stays gone too, since a teammate may have
 * deleted or archived it meanwhile, unless `removedByTheMove`: a move to
 * another project took it off this board itself.
 */
export function restorePlace<T extends { id: string; columnId: string }>(
  current: T[],
  previous: T[],
  ticketId: string,
  { placedIn, removedByTheMove }: { placedIn: string; removedByTheMove: boolean },
): T[] {
  const was = previous.find((t) => t.id === ticketId);
  if (!was) return current;
  const now = current.find((t) => t.id === ticketId);
  if (now ? now.columnId !== placedIn : !removedByTheMove) return current;
  const held = now ? current : [...current, was];
  const stillBeside = new Set(held.filter((t) => t.columnId === was.columnId).map((t) => t.id));
  const oldColumn = previous.filter((t) => t.columnId === was.columnId && (t.id === ticketId || stillBeside.has(t.id)));
  return placeAtAnchor(held, ticketId, was.columnId, dropAnchor(oldColumn, ticketId));
}
